import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, ArrowRight } from "lucide-react";
import { getNearest } from "../lib/api";
import { slugify } from "../lib/seo";

export default function NearMeSearch({ tone = "light" }) {
    const [value, setValue] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const nav = useNavigate();

    const onDark = tone === "dark";

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        const v = value.trim();
        if (!v) {
            setError("Enter a ZIP code or neighborhood.");
            return;
        }
        setLoading(true);
        try {
            const isZip = /^\d{5}$/.test(v);
            const res = await getNearest(isZip ? { zip: v } : { city: v });
            if (res?.matched_city) {
                nav(`/dispensary-near/${slugify(res.matched_city)}`);
            } else if (res?.store) {
                nav(`/locations/${res.store.slug}`);
            } else {
                setError("No store found for that area.");
            }
        } catch (err) {
            setError("Couldn't find a match. Try a NY ZIP or neighborhood.");
        } finally {
            setLoading(false);
        }
    };

    const useGeolocation = () => {
        if (!navigator.geolocation) {
            setError("Geolocation not available in this browser.");
            return;
        }
        setLoading(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const d1 = Math.hypot(
                    pos.coords.latitude - 40.7392,
                    pos.coords.longitude - -73.7106
                );
                const d2 = Math.hypot(
                    pos.coords.latitude - 40.632,
                    pos.coords.longitude - -74.1574
                );
                nav(
                    `/locations/${d1 < d2 ? "queens-long-island" : "staten-island"}`
                );
            },
            () => {
                setError("We couldn't read your location.");
                setLoading(false);
            }
        );
    };

    return (
        <form
            onSubmit={submit}
            data-testid="near-me-search"
            className="w-full max-w-2xl"
        >
            <div className="flex flex-col sm:flex-row items-stretch gap-3">
                <div className="flex-1 relative">
                    <Search
                        size={16}
                        className={`absolute left-4 top-1/2 -translate-y-1/2 ${onDark ? "text-zen-olive-light" : "text-zen-olive"}`}
                    />
                    <input
                        data-testid="near-me-search-input"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder="ZIP code or neighborhood (e.g. 11040, Floral Park)"
                        className={`w-full ${onDark ? "bg-zen-elevated/40 border-zen-cream/20 text-zen-cream placeholder:text-zen-cream-muted focus:border-zen-olive-light" : "bg-white border-zen-ink/15 text-zen-ink placeholder:text-zen-muted focus:border-zen-olive"} border pl-11 pr-4 py-4 text-sm sm:text-base focus:outline-none transition-colors`}
                    />
                </div>
                <button
                    type="submit"
                    data-testid="near-me-search-submit"
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-2 bg-zen-olive hover:bg-zen-olive-dark text-zen-bg px-7 py-4 font-medium uppercase tracking-[0.22em] text-[11px] transition-colors disabled:opacity-60"
                >
                    {loading ? "Searching…" : "Find a dispensary"}
                    {!loading && <ArrowRight size={14} strokeWidth={2.4} />}
                </button>
            </div>
            <div className="mt-4 flex items-center gap-5 text-xs">
                <button
                    type="button"
                    onClick={useGeolocation}
                    data-testid="near-me-geolocate"
                    className={`inline-flex items-center gap-2 uppercase tracking-[0.22em] text-[11px] ${onDark ? "text-zen-olive-light hover:text-zen-cream" : "text-zen-olive hover:text-zen-olive-dark"}`}
                >
                    <MapPin size={12} />
                    Use my location
                </button>
                {error && (
                    <span
                        data-testid="near-me-search-error"
                        className={`text-xs ${onDark ? "text-red-200" : "text-red-600/80"}`}
                    >
                        {error}
                    </span>
                )}
            </div>
        </form>
    );
}
